import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { useStructuredData } from "@/hooks/useStructuredData";
import { SITE_FACTS } from "@shared/siteFacts";

const POSTER = "/video/freedailypro-tour-poster.jpg";
/** The 4-minute tour under the hero, hosted on YouTube. Click to play (never autoplay); until then the page loads only a 23 KB poster. #demo scrolls here. */
export function TourVideo() {
  const [playing, setPlaying] = useState(false);
  const ref = useRef<HTMLElement>(null);

  const yt = SITE_FACTS.youtubeTourId;
  useStructuredData({
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: "FreeDailyPro in 4 minutes: every professional task in one browser tab",
    description: `A guided tour of FreeDailyPro: ${SITE_FACTS.toolCountPhrase}, ${SITE_FACTS.apiCount} API endpoints and ${SITE_FACTS.mcpCount} MCP tools, custom software, privacy-first processing in your browser, and simple pricing.`,
    thumbnailUrl: "https://www.freedailypro.com" + POSTER,
    embedUrl: `https://www.youtube.com/embed/${yt}`,
    contentUrl: `https://www.youtube.com/watch?v=${yt}`,
    uploadDate: "2026-10-28",
    duration: "PT3M58S",
  });

  useEffect(() => {
    if (window.location.hash === "#demo") ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  return (
    <section id="demo" ref={ref} className="container py-10 scroll-mt-24" aria-labelledby="demo-title">
      <div className="max-w-4xl mx-auto text-center mb-6">
        <h2 id="demo-title" className="text-2xl md:text-3xl font-bold tracking-tight gradient-text">See it in {SITE_FACTS.demoVideoMinutes} minutes</h2>
        <p className="text-muted-foreground mt-2">Every professional task, one browser platform: tools, applications, workflow products, API &amp; MCP, and software built for you.</p>
      </div>
      <div className="max-w-4xl mx-auto relative aspect-video rounded-2xl overflow-hidden border border-primary/30 bg-black shadow-lg">
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${yt}?autoplay=1&rel=0`}
            title="FreeDailyPro in 4 minutes"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
          />
        ) : (
          <button type="button" onClick={() => setPlaying(true)} aria-label="Play the 4-minute FreeDailyPro tour" className="group absolute inset-0 w-full h-full">
            <img src={POSTER} alt="" width={1280} height={720} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <span className="absolute inset-x-0 bottom-0 flex justify-center pb-[5%]">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-primary-foreground shadow-xl transition-transform group-hover:scale-105">
                <Play className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" />
                Watch the {SITE_FACTS.demoVideoMinutes}-minute tour
              </span>
            </span>
          </button>
        )}
      </div>
    </section>
  );
}
